import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  combineLatest,
  finalize,
  of,
  Subscription,
  take,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { Moment } from 'moment-timezone';

import { ICapacity, Resource, ResourceAssignedToFloorplan } from '@shared/resource/models';
import { Schedule } from '@shared/schedule/models';
import { describeOutOfBusinessHours } from '@shared/schedule/functions';
import { MergedCustomFields, Party } from '@shared/party/models';
import { BlockedTimeResult } from '@shared/blocked-time/models';
import { ServiceWithAvailability } from '@shared/service/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Restaurant } from '@shared/restaurant/models';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { mergeCustomFields, canBypassNewPartyValidation, partyCriticalFieldsUnchanged, PartyCriticalFields, partyIdsEqual, isPartySeated } from '@shared/party';
import { ResourceSuggestionOption } from '@shared/resource/enums';
import {
  CommonRadioButtonItem,
  ConfigurePartyService,
  CustomDateParserFormatterProvider,
  CustomTimepickerModelAdapterProvider,
  DateTimeService,
  FloorplanResources,
  IParty,
  ProviderScheduleService,
  TelService,
  calculateShortenedDurationState,
  getServiceOptionNameWithWarning,
  ShortenedDurationState,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { CustomerInfo } from '@tablesready/frontend-party-customer-info';
import { WaitTimeService } from '@tablesready/frontend-party-observer';
import { PartiesService } from '@tablesready/frontend-api-services';
import {
  AutoDurationRecalculator,
  DateService,
  MarkPartyService,
  ResourcesService,
  ServicesService,
} from '../../../services';
import { CustomersService } from '../../../customers/services';
import { AddEditPartyService } from '../../../services/add-edit-party.service';

@Component({
  selector: 'tablesready-add-edit-party',
  templateUrl: './add-edit-party.component.html',
  styleUrls: ['./add-edit-party.component.scss'],
  providers: [
    CustomDateParserFormatterProvider,
    CustomTimepickerModelAdapterProvider,
  ],
  standalone: false,
})
export class AddEditPartyComponent implements OnInit, OnDestroy {
  @Input() partyToEdit: IParty;
  @Input() typeOfParty: PartyTypes;
  @Input() userSchedule;
  @Input() user: Restaurant;
  @Input() parties: IParty[];
  @Input() selectedServices;
  @Input() showSelectResourceMessage: boolean;
  @Input() selectedResource: Resource;
  @Input() selectedTime: number;
  @Input() selectedEndTime: number;
  @Input() selectedDate: NgbDateStruct;
  @Input() floorplanResources: FloorplanResources[];
  @Input() availableOnBusinessPlan = false;
  @Input() resourceOverrideCallback?: (party: IParty) => void;
  @Input() afterInit?: (instance: AddEditPartyComponent) => void;

  title: string;
  angularticsActionType: string;
  selectedTimeMode = false;
  isSubmitting = false;
  isSavingParty = false;
  now: number;
  resourcesInDropdown: ResourceAssignedToFloorplan[] = [];
  servicesInDropdown: ServiceWithAvailability[] = [];
  // Shortened duration tracking
  shortenedDurationState: ShortenedDurationState | null = null;
  shortenedDurationConfirmed = false;

  msPerMin = 60000;
  private readonly durationRecalc = new AutoDurationRecalculator({
    getServices: () => this.servicesInDropdown,
    getSelectedServiceIds: () => this.party.services || [],
    getPartySize: () => this.party.size,
    getDuration: () => this.party.duration,
    applyDuration: (duration) => {
      this.party.duration = duration;
      this.party.durationInMinutes = Math.round(duration / this.msPerMin) || 0;
    },
  });
  shortestPhoneDigits = 4;
  originalQuotedTime = 0;
  timezone: string;
  currentTime: Moment;
  isWorkTime = false;
  resourceSizeAlert: string | null = null;
  generalSchedule: Schedule[];
  overrideNoResources = false;
  overrideBlockedTime = false;
  blockedTime?: BlockedTimeResult;
  exceptions;
  reservationMinutesPast = 420;
  sizeOther: number;
  icon = AppIcon;
  party;
  time;
  date;
  workHours: Array<[number, number]>;
  showOverlappingAlert;
  showResourceAlert = false;
  availablePartyDuration;
  availablePartyDurationInMin;
  removeServices = false;
  removeResources = false;
  showSizeValidationMessage;
  schedule;
  overrideIsWorkTime = false;
  // Date
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  noteOptions: string[] | string;
  serviceSettingsLabel: string;
  resourceSettingsLabel: string;
  displayOptionCustomerLabel: string;
  displayOptionsReservationsLabel: string;
  displayOptionsReservationsLabelPlural: string;
  sizesRadioItems: CommonRadioButtonItem[] = [];
  servicesEnabled = false;
  partySources = PartySource;
  resourceSuggestionOption = ResourceSuggestionOption;
  customer: CustomerInfo = null;

  private overlappingParty: Party[];
  private allowMultipleParties: boolean;
  private resourcesEnabled = false;
  private onlineReservationEnabled: boolean;
  private isEditMode = false;
  private originalCritical: PartyCriticalFields | null = null;
  private hasDisabledServicesOnInit = false;

  @ViewChild('confirmModalContent') confirmModalContentRef: ElementRef;
  @ViewChild('addEditForm') addEditForm: NgForm;

  private readonly subscription = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly waitTimeService: WaitTimeService,
    private readonly dateService: DateService,
    private readonly partiesService: PartiesService,
    private readonly configureParty: ConfigurePartyService,
    private readonly clockService: ClockService,
    private readonly telService: TelService,
    private readonly dateTimeService: DateTimeService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly markPartyService: MarkPartyService,
    private readonly customerService: CustomersService,
    private readonly addEditPartyService: AddEditPartyService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
  ) { }

  isOutOfBusinessHours(formGroup: NgForm): boolean {
    if (this.isEditMode && isPartySeated(this.partyToEdit)) {
      return false;
    }

    return (!this.isWorkTime || this.isPartyInPastTime) && this.isTimeFieldValid(formGroup);
  }

  private canBypassWorkTime(): boolean {
    return canBypassNewPartyValidation(
      this.isEditMode, this.partyToEdit, this.originalCritical, () => this.snapshotCritical(),
    );
  }

  private captureOriginalCritical(): void {
    this.originalCritical = this.snapshotCritical();
  }

  private criticalFieldsUnchanged(): boolean {
    return partyCriticalFieldsUnchanged(this.originalCritical, () => this.snapshotCritical());
  }

  // quotedTime uses currentTime (the live, edited reservation time). this.party.quoted_time is only
  // written at save time (convertQuotedTime), so it is stale during editing.
  private snapshotCritical(): PartyCriticalFields {
    return {
      size: this.party.size,
      duration: this.party.duration,
      quotedTime: this.currentTime.valueOf(),
      services: [...(this.party.services ?? [])],
      resources: [...(this.party.resources ?? [])],
      seatedTime: this.partyToEdit?.seated_time ?? null,
      checkinTime: this.partyToEdit?.checkin_time ?? null,
    };
  }

  isTimeFieldValid({ form: addEditForm }: NgForm): boolean {
    const time = addEditForm.get('time')?.value;

    return time !== '' && time !== undefined;
  }

  noAvailableServices(formGroup: NgForm): boolean {
    // A seated party is never re-validated against current availability (it was necessarily seated
    // in the past), so an empty dropdown says nothing about it — and this warning offers no
    // override, so leaving it on would block the edit outright.
    return this.servicesInDropdown.length === 0 && this.servicesEnabled &&
      this.isTimeFieldValid(formGroup) && !this.isOutOfBusinessHours(formGroup) &&
      !isPartySeated(this.partyToEdit);
  }

  get isAvailableTime() {
    return this.isWorkTime &&
      this.showResourceAlert &&
      !this.resourcesInDropdown.length &&
      this.resourcesEnabled;
  }

  get isWalkin() {
    return this.typeOfParty === PartyTypes.walkIn;
  }

  get isReservation() {
    return this.typeOfParty === PartyTypes.reservation;
  }

  get isPartyInPastTime() {
    return this.currentTime.valueOf() < Date.now();
  }

  getOutOfBusinessHoursMessage(): string {
    return describeOutOfBusinessHours(
      this.currentTime.valueOf(),
      this.party?.duration || 0,
      this.workHours || [],
      this.timezone,
    );
  }

  ngOnInit(): void {
    this.sizesRadioItems = [1, 2, 3, 4, 5, 6, 7].map((size) => ({
      name: String(size),
      value: size,
      onClick: () => this.sizeChanged(),
    }));

    this.subscription.add(this.clockService.clock$.subscribe(now => this.now = now));

    this.initModalProp();
    this.minDate = this.dateTimeService.momentToNgbDate(this.currentTime);
    this.maxDate = {
      ...this.minDate,
      year: this.minDate.year + 1,
    };
    if (this.partyToEdit) {
      this.initEditMode();
      this.checkWorkTime();
    } else if (this.selectedTime && this.selectedResource) {
      this.initAddModeWithSelectedResourceAndTime(this.selectedTime);
    } else {
      this.initAddMode();
    }
    this.setTitle();

    if (this.afterInit) {
      this.afterInit(this);
    }
  }

  initModalProp() {
    this.noteOptions = this.user?.noteOptions || [];
    this.serviceSettingsLabel = this.user?.serviceSettings?.label || 'service';
    this.resourceSettingsLabel = this.user.resourceSettings?.label || 'resource';
    this.displayOptionCustomerLabel = this.user.displayOptions?.labels?.customer || 'customer';
    this.displayOptionsReservationsLabel = this.user.displayOptions?.labels?.reservation || 'reservation';
    this.displayOptionsReservationsLabelPlural = this.user.displayOptions?.labels?.reservation_plural || 'reservations';
    this.allowMultipleParties = this.user?.bookingOptions?.allowMultipleParties;
    this.onlineReservationEnabled = this.user?.bookingOptions?.onlineReservationsEnabled;

    this.generalSchedule = (this.userSchedule || []).filter(schedule => schedule.default);

    try {
      this.schedule = this.generalSchedule[0].hours;
    } catch (e) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Please fill in work schedule in Settings -> Schedules',
        body: '',
      });
    }

    this.timezone = this.user.timezone;
    this.currentTime = moment.tz(this.timezone);
    this.exceptions = this.generalSchedule[0]?.exceptions || [];
    this.showSelectResourceMessage = this.showSelectResourceMessage || false;

    const subscription = combineLatest([
      this.resourcesService.isEnabled(),
      this.servicesService.isEnabled(),
    ])
      .pipe(tap(([resourcesEnabled, servicesEnabled]) => {
        this.resourcesEnabled = resourcesEnabled;
        this.servicesEnabled = servicesEnabled;
        this.overrideNoResources = !(this.servicesEnabled || this.user.bookingOptions?.onlineReservationsEnabled);
      }))
      .subscribe();
    this.subscription.add(subscription);
  }

  setTitle(): void {
    let text = this.user?.displayOptions?.labels?.customer || 'customer';

    if (this.typeOfParty === PartyTypes.reservation) {
      text = this.user.displayOptions?.labels?.reservation || 'reservation';
    }

    this.title += text;
  }

  initEditMode() {
    this.title = 'Edit ';
    this.angularticsActionType = 'edited reservation';
    this.party = cloneDeep(this.partyToEdit);
    this.selectedTimeMode = false;
    this.isEditMode = true;
    const quotedTime = moment.tz(this.party.quoted_time, this.timezone);
    // Only enabled services/resources are shown as selected and offered in the dropdown — a
    // disabled assignment is never user-visible or user-selectable. The true original ids
    // (this.partyToEdit.services/.resources) remain available for the autoselect guard below and
    // for saveParty(), which restores them on save if the user never touched these fields, so a
    // disabled assignment is preserved rather than silently dropped by an unrelated edit.
    const enabledServices = this.servicesService.getEnabledByIds(this.partyToEdit.services);
    this.hasDisabledServicesOnInit = enabledServices.length !== (this.partyToEdit.services || []).length;
    this.party.services = enabledServices.map(service => service._id);
    if (this.hasDisabledServicesOnInit) {
      this.party.duration = this.dateService.checkServicesDuration(enabledServices, this.party.services, this.party.size);
    }
    this.party.resources = this.resourcesService.getEnabledByIds(this.partyToEdit.resources).map(resource => resource._id);
    this.setPartyDurationInMinutes();
    this.durationRecalc.setBaseline(this.dateService.checkServicesDuration(enabledServices, this.party.services, this.party.size), this.party.services);
    this.party.phone = this.telService.format(this.party.phone, this.user.country, 'custom');
    this.party.quoted_time_minutes = this.typeOfParty === PartyTypes.walkIn ? Math.round((this.party.quoted_time - this.now) / this.msPerMin) : 0;
    this.party.date = this.dateTimeService.momentToNgbDate(quotedTime);
    this.party.time = quotedTime.format('h:mm A');
    this.originalQuotedTime = this.party.quoted_time;
    this.currentTime = moment(this.party.quoted_time).tz(this.timezone);
    this.setPartySize(this.party.size);
    this.isWorkTime = true;
    this.captureOriginalCritical();
  }

  setPartySize(size) {
    if (size < 8) {
      this.party.size = size;

      return true;
    }
    this.sizeOther = this.party.size;
  }

  initAddModeWithSelectedResourceAndTime(time: number): void {
    this.title = 'Add ';
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = true;
    const selectedTime = this.dateTimeService.roundingMinutes(Number(time), 15, this.timezone);
    this.party = {
      quoted_time: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: Math.max(this.selectedResource.capacity.min, Math.min(this.user.default_size, this.selectedResource.capacity.max)),
      type: this.getPartyType(this.typeOfParty),
      date: this.selectedDate,
      time: selectedTime.format('h:mm A'),
      resources: [this.selectedResource._id],
      services: [],
      customFields: {},
    };
    this.currentTime = selectedTime;
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  initAddMode() {
    this.title = 'Add ';
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = false;
    this.party = {
      quoted_time_minutes: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: this.user.default_size,
      type: this.getPartyType(this.typeOfParty),
      date: this.selectedDate,
      customFields: {},
    };
    this.party.quoted_time = this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  getPartyType(typeOfParty: PartyTypes): PartyTypes {
    return typeOfParty === PartyTypes.reservation ? PartyTypes.reservation : PartyTypes.walkIn;
  }

  checkWorkTime(): boolean {
    if (!this.servicesEnabled && this.resourcesEnabled) {
      this.getAvailableResourcesServices(this.currentTime.format('x'), this.party);
    }
    if (this.typeOfParty === PartyTypes.reservation && !this.party.time) {
      if (this.resourceOverrideCallback) {
        this.resourceOverrideCallback(this.party);
      }

      return false;
    }
    this.isSubmitting = true;
    this.time = this.party.time || this.currentTime.format('h:mm A');
    this.date = this.currentTime.format('D-M-YYYY');

    const { hours, exceptions } = this.generalSchedule[0];
    this.workHours = this.providerScheduleService
      .getWorkingHours(hours, exceptions, this.currentTime.valueOf(), this.timezone);

    if (this.party.duration <= 0 && !this.criticalFieldsUnchanged()) {
      // Recomputing from the enabled-only dropdown would silently rewrite the duration of an
      // existing party whose availability-affecting fields the user never touched.
      this.party.duration = this.dateService.checkServicesDuration(this.servicesInDropdown, this.party.services, this.party.size);
    }

    this.setPartyDurationInMinutes();

    this.isWorkTime = this.providerScheduleService
      .comparisonTime(this.currentTime.valueOf(), this.workHours, this.party.duration);
    if (this.servicesEnabled || this.resourcesEnabled) {
      this.getAvailableResourcesServices(this.currentTime.format('x'), this.party);
    } else {
      this.isSubmitting = false;
    }
  }

  getAvailableResourcesServices(date, party) {
    const { services, resources, size, duration } = party;
    if (!date && !services && !resources) {
      return false;
    }

    const type = party.type === PartyTypes.reservation ? party.type : PartyTypes.walkIn;
    const dateMilliseconds = this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone).valueOf();
    const { hours, exceptions } = this.generalSchedule[0];
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      dateMilliseconds,
      this.timezone,
    );

    this.partiesService.findResourcesServicesForNewParty(
      date,
      type,
      services,
      resources,
      size,
      duration,
      party._id,
      this.overrideNoResources,
      hoursWithDate,
    )
      .pipe(
        take(1),
        tap((dataInput) => {
          const data = dataInput as any;

          let resourcesCapacity: ICapacity = {
            min: 0,
            max: 0,
          };
          this.showOverlappingAlert = false;
          this.overlappingParty = data?.overlappingParty;
          this.blockedTime = data?.blockedTime;

          if (data?.availableResources) {
            this.resourcesInDropdown = this.addEditPartyService.placeResourcesFitBySizeFirst<ResourceAssignedToFloorplan>(data.availableResources, size);
            // If party.resource is already chosen and does not belong to the resources list (e.g. it
            // was disabled), clear it — but only when something critical actually changed. An edit
            // that never touches services/resources/size/duration/time must leave the existing
            // (possibly since-disabled) assignment alone.
            if (this.resourcesInDropdown.length && party.resources && !this.canBypassWorkTime()) {
              party.resources = party.resources.filter((resourceId) => {
                const is = this.resourcesInDropdown.find(resource => resource._id === resourceId);

                return Boolean(is);
              });
            }

            if (this.resourcesEnabled) {
              this.showResourceAlert = true;
            }

            resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
          }

          if (data?.availableServices) {
            this.servicesInDropdown = data.availableServices;

            if (this.party.services && this.party.services.length > 0 && this.handleShortenedAlert()) {
              this.checkWorkTime();

              return;
            }
          }

          if (!this.overlappingParty) {
            this.overlappingParty = [];
          }

          if (this.overlappingParty.length && this.onlineReservationEnabled) {
            if (this.allowMultipleParties || this.overrideNoResources) {
              this.showSizeAlert(resourcesCapacity);
            } else {
              this.showOverlappingAlert = true;
              const overlappingParty = this.overlappingParty[0];
              this.availablePartyDuration = overlappingParty.quoted_time - date;
              this.availablePartyDurationInMin = Math.round(this.availablePartyDuration / this.msPerMin);
            }
          }

          // Protect an existing party's already-assigned service/resource from being silently filled
          // in by this autoselect. These check the party's true original assignment
          // (this.partyToEdit), not the enabled-only `this.party.services`/`.resources` shown in the
          // dropdown — a disabled assignment is hidden from the picker, but it still counts as
          // "already assigned" for this guard, and only counts as absent when the party truly never
          // had one.
          const blocksServiceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.services?.length ?? 0) > 0;
          const blocksResourceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.resources?.length ?? 0) > 0;
          if (this.servicesInDropdown?.length === 1 && !this.party?.services?.length && !this.removeServices && !blocksServiceAutoselect) {
            const serviceIdToSelect = this.servicesInDropdown[0]._id;
            this.party.services = [serviceIdToSelect];

            if (
              this.resourcesEnabled &&
              this.user.resourceSettings?.resourceSuggestion === this.resourceSuggestionOption.auto &&
              this.resourcesInDropdown.length &&
              !this.removeResources &&
              !this.selectedResource &&
              !blocksResourceAutoselect
            ) {
              let resourceIdToSelect: string;

              for (const availableResource of data?.availableResources) {
                if (availableResource.services.includes(serviceIdToSelect) &&
                  availableResource.capacity.max >= size) {
                  // We don't want to pre-select a resource that doesn't fit party size
                  // And we've already filtered resources by min capacity
                  resourceIdToSelect = availableResource._id;

                  break;
                }
              }

              if (resourceIdToSelect !== undefined) {
                this.party.resources = [resourceIdToSelect];
                this.selectResources();
              }
            }
            this.selectServices();
            if (this.resourcesEnabled && this.party?.resources?.length) {
              this.selectResources();
            }
          }
          this.isSubmitting = false;

          if (this.resourceOverrideCallback) {
            this.resourceOverrideCallback(this.party);
          }
        }),
        catchError((error) => {
          if (error.status === 401) {
            this.cancel();
          } else {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: `resources and services not found: ${error}`,
            });
          }

          return of(error);
        }),
      )
      .subscribe();
  }

  calculateCapacityInSelectedResources(resources = [], selectedResources = []) {
    const fullDataOfSelectedResources = resources.filter(resource => selectedResources.includes(resource._id));

    return this.calculateResourcesCapacity(fullDataOfSelectedResources);
  }

  calculateResourcesCapacity(resources = []) {
    return resources.reduce((acc, resource) => {
      acc.min += resource.capacity.min || 0;
      acc.max += resource.capacity.max || 0;

      return acc;
    }, {
      min: 0,
      max: 0,
    });
  }

  selectServices() {
    if (this.party.services && this.party.services.length === 0) {
      // Reset errors if services removed from party
      this.removeServices = true;
      this.showResourceAlert = false;
      this.shortenedDurationState = null;
      this.durationRecalc.recompute();
      this.checkWorkTime();

      return;
    }
    this.removeServices = false;
    if (this.resourcesEnabled) {
      this.showResourceAlert = true;
    }
    this.party.duration = 0;
    this.durationRecalc.recompute();

    this.handleShortenedAlert();
    this.checkWorkTime();
  }

  private handleShortenedAlert(): boolean {
    let shortenedDurationChanged = false;
    const prevRequiresConfirmation = this.shortenedDurationState?.requiresConfirmation ?? false;
    this.shortenedDurationState = calculateShortenedDurationState(this.servicesInDropdown, this.party?.services || []);

    // In edit mode the adjusted duration is never written back for a party that bypasses new-party
    // validation (seated, or nothing availability-affecting changed) — that would truncate an
    // already-saved duration; the state is still computed so the warning can be displayed.
    if (!this.canBypassWorkTime()
        && this.shortenedDurationState.adjustedDurationMs !== null
        && (!this.party.duration || this.party.duration > this.shortenedDurationState.adjustedDurationMs)) {
      this.party.duration = this.shortenedDurationState.adjustedDurationMs;
      this.party.durationInMinutes = Math.round(this.shortenedDurationState.adjustedDurationMs / this.msPerMin);
      shortenedDurationChanged = true;
    }

    if (!this.shortenedDurationState.requiresConfirmation) {
      this.shortenedDurationConfirmed = true;
    } else if (!prevRequiresConfirmation) {
      this.shortenedDurationConfirmed = false;
    }

    return shortenedDurationChanged;
  }

  doesEditingHaveAnyConflicts(): Record<string, boolean> {
    return {
      editFormInvalid: this.addEditForm.form.invalid,
      isOutOutBusinessHours: (!this.isWorkTime || this.isPartyInPastTime) && !this.overrideIsWorkTime,
      isAvailableTimeAndNoOverride: this.isAvailableTime && !this.overrideNoResources,
      noAvailService: this.noAvailableServices(this.addEditForm),
      noConfirm: this.isConfirmPartyButtonDisabled(),
      formPending: this.addEditForm.form.pending,
      submitting: this.isSubmitting,
    };
  }

  addResourceToSelectedOnes(resourceId: string, services: string[]): void {
    if (this.party.resources?.length === 0) {
      this.party.resources = [resourceId];
      this.selectResources();

      return;
    }

    const doesExist = this.party?.resources?.findIndex((id) => id === resourceId);
    if (doesExist !== -1) {
      if (this.resourceOverrideCallback) {
        this.resourceOverrideCallback(this.party);
      }

      return;
    }

    this.party.resources = [...new Set([...this.party.resources, resourceId])];
    this.party.services = [...new Set([...this.party.services, ...services])];
    this.selectResources();
  }

  selectResources(): void {
    this.durationRecalc.recompute();
    this.showResourceAlert = true;
    if ((this.party.resources || []).length) {
      this.removeResources = false;
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, this.party.resources);
      this.showSizeAlert(resourcesCapacity);
    } else {
      this.removeResources = true;
    }
    this.checkWorkTime();
  }

  overrideResources() {
    this.checkWorkTime();
  }

  clearServicesResources() {
    this.party.services = [];
    this.party.resources = [];
  }

  setAvailableDuration() {
    this.party.duration = this.availablePartyDuration;
    this.party.durationInMinutes = this.availablePartyDurationInMin;
    this.showOverlappingAlert = false;
    this.availablePartyDuration = 0;
  }

  setQuotedTime() {
    this.currentTime = moment.tz(this.timezone);
    this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.clearServicesResources();
    this.checkWorkTime();
  }

  changeQuoted(num, model = 'quoted_time_minutes', modelToAddMilliseconds = '') {
    this.addEditForm.form.markAsDirty();
    this.party[model] += num;
    if (modelToAddMilliseconds) {
      this.party[modelToAddMilliseconds] += num * this.msPerMin;
    } else {
      this.clearServicesResources();
    }
    if (model === 'quoted_time_minutes') {
      this.currentTime.add(num, 'minutes');
    }
    this.checkWorkTime();
  }

  convertQuotedTime(party) {
    const resTime = moment.tz(this.timezone);

    delete party.date;
    delete party.time;
    delete party.durationInMinutes;
    delete party.quoted_time_minutes;
    delete party.className;
    delete party.assignedResources;

    if (this.party.type === PartyTypes.reservation && this.currentTime.format('x') < resTime.add(-this.reservationMinutesPast, 'minutes').format('x')) {
      party.quoted_time = Number(this.currentTime.format('x'));
      this.isWorkTime = false;

      return;
    }

    const updateWaitTime = !party._id && party.type === PartyTypes.walkIn; // Update wait time if new party
    party.quoted_time = party.quoted_time || 0;
    if (updateWaitTime && !(
      this.originalQuotedTime > 0 &&
      party.quoted_time > this.originalQuotedTime - this.msPerMin &&
      party.quoted_time < this.originalQuotedTime + this.msPerMin
    )) {
      this.waitTimeService.setTime(Math.round((party.quoted_time - this.now) / this.msPerMin));
    }
    party.quoted_time = Number(this.currentTime.format('x'));
  }

  showSizeAlert(resourcesCapacity: ICapacity): void {
    this.resourceSizeAlert = this.resourcesInDropdown?.length
      ? this.configureParty.buildResourceSizeAlert({
          actualSize: this.party?.size,
          capacity: resourcesCapacity,
          customerLabel: this.displayOptionCustomerLabel,
          resourceLabel: this.resourceSettingsLabel,
          allowMultipleParties: this.allowMultipleParties,
          partySizeForTimeSlots: this.user?.bookingOptions?.partySizeForTimeSlots,
          overlappingParties: this.overlappingParty,
        })
      : null;
  }

  sizeChanged() {
    this.showSizeValidationMessage = false;
    this.resourceSizeAlert = null;
    const { resources, size } = this.party;
    if (this.resourcesEnabled && this.party?.resources?.length) {
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
      this.showSizeAlert(resourcesCapacity);
      this.sizeOther = size < 8 ? '' : size;

      return false;
    }
    this.checkWorkTime();
  }

  changeSize() {
    this.showSizeValidationMessage = false;
    this.party.size = this.sizeOther || this.user.default_size;
    this.sizeChanged();
  }

  partyPhoneLookup() {
    const { phone } = this.party;
    if (!phone ||
      phone === '' ||
      typeof phone === 'undefined' ||
      phone.length < this.shortestPhoneDigits
    ) {
      return;
    }
    const parsedPhone = this.telService.format(phone, this.user.country, 'intl');
    if (typeof parsedPhone !== 'undefined' && parsedPhone.trim().length >= this.shortestPhoneDigits) {
      this.customerService.getByPhone(parsedPhone)
        .pipe(
          tap(data => {
            if (data) {
              const customerCustomFields: MergedCustomFields = extractPartyFieldsFromCustomer(this.user.partyFields, data);
              const _data = {
                name: this.party.name || customerCustomFields.name,
                customFields: mergeCustomFields(this.party.customFields, customerCustomFields.customFields),
              };
              this.party = {
                ...this.party,
                ..._data,
              };
              this.customer = data;
            } else {
              this.customer = null;
            }
          }),
        )
        .subscribe();
    }
  }

  getItemOptionName = (item: ResourceAssignedToFloorplan): string => {
    return this.getItemName(item, true);
  };

  getItemName(item: ResourceAssignedToFloorplan, addCapacity = false): string {
    let result = item?.name || '';

    if (addCapacity) {
      result += ` (${item.capacity.max}) `;
    }

    if (item?.onFloorplan) {
      result += ` [${item.onFloorplan}]`;
    }

    return result;
  }

  changeDuration() {
    this.party.duration = this.party.durationInMinutes * this.msPerMin;
    this.checkWorkTime();
  }

  isConfirmPartyButtonDisabled() {
    return (this.showOverlappingAlert || !!this.resourceSizeAlert);
  }

  changeDate() {
    if (
      this.selectedTimeMode ||
      !this.party.time ||
      !this.party.time.includes('M') ||
      typeof this.party.date === 'string' ||
      this.party.date === null
    ) {
      return false;
    }

    this.currentTime = this.dateTimeService.setTimeToDate(
      this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone),
      this.party.time,
      this.timezone,
    );

    if (this.servicesEnabled && this.onlineReservationEnabled && !this.resourcesEnabled) {
      this.clearServicesResources();
    }
    this.overrideIsWorkTime = false;
    this.checkWorkTime();
  }

  clean(obj) {
    for (const propName in obj) {
      if (propName === 'messagingProvider') {
        continue;
      }

      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
  }

  saveParty(): void {
    const party = { ...this.party };
    this.clean(party);
    if (party.phone) {
      party.phone = this.telService.format(party.phone, this.user.country, 'intl');
    }

    this.convertQuotedTime(party);
    // Refuse the save only while the schedule warning and its "Override?" checkbox are actually on
    // screen (checkIfOutOfBusinessHours() is that warning's condition) — otherwise Confirm would
    // look enabled and silently do nothing.
    if (this.checkIfOutOfBusinessHours(this.addEditForm)) {
      this.isSubmitting = false;

      return;
    }

    if (party.resource && !(party.resources || []).length) {
      if (!Array.isArray(party.resource)) {
        party.resources = [party.resource];
      } else {
        party.resources = party.resource;
      }
      delete party.resource;
    }

    // The services/resources fields only ever show/offer enabled ids, so a disabled assignment is
    // never visible to touch. If the user left a field exactly as it was when the form opened,
    // restore the party's true original ids (which may include a since-disabled one) rather than
    // saving the enabled-only value the picker displayed — an edit to an unrelated field (e.g.
    // Notes) must not silently drop a disabled service/resource assignment.
    if (this.isEditMode) {
      if (partyIdsEqual(party.services, this.originalCritical?.services)) {
        party.services = this.partyToEdit.services || [];
      }
      if (partyIdsEqual(party.resources, this.originalCritical?.resources)) {
        party.resources = this.partyToEdit.resources || [];
      }
      // hasDisabledServicesOnInit means initEditMode recalculated duration from only the
      // enabled subset of services (possibly 0), overwriting the party's true duration in the
      // form. If the user never touched the duration field since, save the true original value
      // instead of the recalculated one, or a disabled-then-reenabled service permanently loses
      // its duration.
      if (this.hasDisabledServicesOnInit && party.duration === this.originalCritical?.duration) {
        party.duration = this.partyToEdit.duration;
      }
    }

    party.overrideNoResources = this.overrideNoResources;
    party.overrideBlockedTime = this.overrideBlockedTime;

    if ((!this.showOverlappingAlert && this.isEditMode) || this.canBypassWorkTime()) {
      party.overrideNoResources = true;
    }

    if (party.type !== PartyTypes.reservation && !Boolean(this.partyToEdit)) {
      party.checkin_time = party.checkin_time || Date.now();
    }

    this.savePartyAction(party);
  }

  cancel(): void {
    void this.modalController.dismiss(null);
  }

  ok(showSuccessUpdateAlert = true): void {
    const { phone } = this.party;
    if (phone !== '' && this.parties.find(party => party.phone === phone)) {
      this.modalController.create<unknown, string>(this.confirmModalContentRef.nativeElement)
        .pipe(
          take(1),
          tap((res) => {
            if (res === 'Cancel click') {
              return;
            }

            this.actionAfterCloseModal();
          }),
          catchError((error) => {
            this.cancel();

            return of(error);
          }),
        )
        .subscribe();

      return;
    }
    this.actionAfterCloseModal();
  }

  actionAfterCloseModal(): void {
    const { size, sizeOther } = this.party;
    if (this.user.waitFields.size &&
      size &&
      typeof size !== 'number' ||
      sizeOther &&
      typeof sizeOther !== 'number') {
      this.showSizeValidationMessage = true;

      return;
    }
    this.isSubmitting = true;
    this.saveParty();
  }

  isSubmitDisabled(addEditForm: NgForm): boolean {
    if (!addEditForm) {
      return;
    }

    const isAvailableTimeAndNoOverride = Boolean(this.isAvailableTime) && !this.overrideNoResources;
    const isBlockedTimeAndNoOverride =
      this.isReservation && Boolean(this.blockedTime?.blocked) && !this.overrideBlockedTime;
    const isOutOutBusinessHours = this.checkIfOutOfBusinessHours(addEditForm);
    const needsShortenedDurationConfirmation =
      Boolean(this.shortenedDurationState?.requiresConfirmation) && !this.shortenedDurationConfirmed;

    return addEditForm.form.invalid ||
      isOutOutBusinessHours ||
      this.noAvailableServices(addEditForm) ||
      this.isConfirmPartyButtonDisabled() ||
      addEditForm.form.pending ||
      this.isSubmitting ||
      isAvailableTimeAndNoOverride ||
      isBlockedTimeAndNoOverride ||
      needsShortenedDurationConfirmation;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  undoCheckIn(): void {
    const party = cloneDeep(this.partyToEdit);
    party.checkin_time = null;
    party.overrideNoResources = this.overrideNoResources;

    if (this.isEditMode) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party, 'check-in was canceled');
  }

  undoServed(): void {
    this.isSubmitting = true;
    const options = {
      party_id: this.partyToEdit._id,
      seated_time: null,
      canceled_time: null,
    };

    this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            `moved to ${this.partyToEdit.type === PartyTypes.togo ? 'To Go' : 'waitlist'}`,
            'Success: ',
          );
          void this.modalController.dismiss();
          this.isSubmitting = false;
        }),
        catchError(err => {
          this.isSubmitting = false;
          this.alertsService.addError('an error occurred during moving party to Waitlist');

          return of(err);
        }),
      ).subscribe();
  }

  checkIfOutOfBusinessHours(addEditForm: NgForm): boolean {
    return this.isOutOfBusinessHours(addEditForm) && !this.overrideIsWorkTime;
  }

  setPartyDurationInMinutes() {
    this.party.durationInMinutes = Math.round(this.party.duration / this.msPerMin) || 0;
  }

  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }

  isUsOrCaPhoneNumber(): boolean {
    return isUsOrCaPhoneNumber(this.party.phone);
  }

  changeMessagingProvider() {
    if (!this.party.phone || isUsOrCaPhoneNumber(this.party.phone)) {
      this.party.messagingProvider = null;
    }
  }

  private savePartyAction(party: IParty, successMessage = 'updated successfully'): void {
    if (this.isSavingParty) {
      return;
    }

    this.isSubmitting = true;
    this.isSavingParty = true;
    this.partiesService.saveParty(party)
      .pipe(
        take(1),
        tap((data) => {
          void this.modalController.dismiss(data);
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: successMessage,
          });
        }),
        catchError((err) => {
          if (err?.reloadData) {
            this.party.resources = [];
            this.checkWorkTime();
          }

          const customer = this.user.displayOptions?.labels?.customer || 'customer';
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `new ${customer} not added successfully: ${err?.message}`,
          });

          if (this.resourceOverrideCallback) {
            this.resourceOverrideCallback(this.party);
          }

          return of(err);
        }),
        finalize(() => {
          this.isSavingParty = false;
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  getServiceOptionName = (service: ServiceWithAvailability): string => {
    return getServiceOptionNameWithWarning(service);
  };
}
